/**
 * LLM Cockpit settings API wrapper.
 *
 * Consumes the 7 vault-routed endpoints at /api/v1/settings/llm/* that the
 * Seitiate engine ships (per SETTINGS-API-CONTRACT-MATRIX-V1 post-correction).
 * These replace the phantom /auth/byok surface. Secrets flow through password
 * fields directly to the engine's VaultCrypto-backed key lifecycle — satisfies
 * principle 6c without needing a separate vault.createEntry call.
 */

import { buildEngineHeaders } from '@/lib/auth';
import { SEITIATE_ENGINE_URL } from '@/lib/env';
import type { ProviderDataChoiceState } from '@/api/providerDataChoice';

export interface LLMProviderCard {
  provider_id: string;
  display_name: string;
  provider_type: string;
  group_id: string;
  group_label: string;
  status: string;
  status_reason?: string | null;
  model_id?: string | null;
  model_display_name?: string | null;
  docs_url?: string | null;
  has_stored_key: boolean;
  uses_env_key: boolean;
  cli_detected: boolean;
  configured: boolean;
  supports_test: boolean;
  latest_status_code?: number | null;
  latest_error?: string | null;
}

export interface LLMProviderGroup {
  id: string;
  label: string;
  providers: LLMProviderCard[];
}

export interface LLMProviderDashboard {
  ready_count: number;
  not_configured_count: number;
  unavailable_count: number;
  groups: LLMProviderGroup[];
}

export interface LLMProviderKeyResponse {
  provider_id: string;
  configured: boolean;
  label?: string | null;
}

export interface LLMProviderTestResult {
  provider_id: string;
  ok: boolean;
  latency_ms: number;
  status: string;
  message: string;
}

/**
 * Advisory provider data-policy observation, joined onto routing options by
 * the engine. Advisory only — never a routing, eligibility or billing input.
 */
export interface AdvisoryDataPolicy {
  authority: 'advisory';
  label: string;
  provider_id: string;
  keeps: string;
  trains: string;
  region: string;
  source_url: string;
  checked_on: string | null;
  note: string;
  verified_by: string;
  last_checked_days_ago: number | null;
  staleness_marker: string | null;
}

export interface LLMModelOption {
  provider_id: string;
  model_id: string;
  label: string;
  quality_tier: string;
  requires_api_key: boolean;
  preset: boolean;
  /** Present on engines that join the advisory column (model_data_policy). */
  data_policy?: AdvisoryDataPolicy | null;
}

export interface LLMTaskMapping {
  action_type: string;
  label: string;
  description: string;
  provider_id: string;
  model_id: string;
  is_override: boolean;
}

export interface LLMTaskMappingsResponse {
  tasks: LLMTaskMapping[];
  options: LLMModelOption[];
}

export interface LLMProviderSpendItem {
  provider_id: string;
  display_name: string;
  today_cost_usd: number;
  week_cost_usd: number;
  month_cost_usd: number;
  month_call_count: number;
}

export interface LLMCallModes {
  subscription_proxy_calls: number;
  byok_calls: number;
  embedded_calls: number;
}

export interface LLMCostSummary {
  days: number;
  total_cost_usd: number;
  budget_limit_usd: number;
  avg_daily_cost_usd: number;
  daily_trends: Array<{
    date: string;
    total_cost_usd: number;
    call_count: number;
    top_action?: string | null;
  }>;
  by_action: Array<{
    action_type: string;
    total_cost_usd: number;
    call_count: number;
    avg_cost_usd: number;
    pct_of_total: number;
  }>;
  anomaly: {
    current_24h_cost: number;
    avg_daily_cost: number;
    ratio: number;
    top_action: string;
  };
}

export interface LLMCostSummaryResponse {
  summary: LLMCostSummary;
  provider_spend: LLMProviderSpendItem[];
  call_modes: LLMCallModes;
  projected_monthly_cost_usd: number;
}

export class LLMSettingsApiError extends Error {
  status: number;
  detail?: string;
  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = 'LLMSettingsApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * The one prefix every read and write in this module lives under: the engine
 * mounts its LLM-settings router at `/api/v1/settings/llm`
 * (`api_router.include_router(llm_settings_router, prefix="/settings/llm")`).
 * Named once here so no call site can drift from it — the combined
 * provider-settings read (seitiate#399) answers at
 * `${LLM_SETTINGS_API_PREFIX}/provider-settings`.
 */
export const LLM_SETTINGS_API_PREFIX = '/api/v1/settings/llm';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = buildEngineHeaders({ extra: init.headers, includeContentType: true, body: init.body });
  const response = await fetch(`${SEITIATE_ENGINE_URL}${LLM_SETTINGS_API_PREFIX}${path}`, { ...init, headers });
  if (response.status === 204) return undefined as T;
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new LLMSettingsApiError(`Request failed: ${response.status}`, response.status);
    }
    return undefined as T;
  }
  if (!response.ok) {
    const detail = (data as { detail?: unknown } | null)?.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail
              .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d)))
              .join('; ')
          : `Request failed: ${response.status}`;
    throw new LLMSettingsApiError(message, response.status, message);
  }
  return data as T;
}

export function getLLMProviderDashboard(options?: { signal?: AbortSignal }): Promise<LLMProviderDashboard> {
  return request<LLMProviderDashboard>('/providers', { signal: options?.signal });
}

export function saveLLMProviderKey(
  providerId: string,
  apiKey: string,
  label?: string,
): Promise<LLMProviderKeyResponse> {
  return request<LLMProviderKeyResponse>(`/providers/${encodeURIComponent(providerId)}/key`, {
    method: 'POST',
    body: JSON.stringify({ api_key: apiKey, label: label ?? null }),
  });
}

export function deleteLLMProviderKey(providerId: string): Promise<void> {
  return request<void>(`/providers/${encodeURIComponent(providerId)}/key`, { method: 'DELETE' });
}

export function testLLMProvider(providerId: string): Promise<LLMProviderTestResult> {
  return request<LLMProviderTestResult>(`/providers/${encodeURIComponent(providerId)}/test`, {
    method: 'POST',
  });
}

export function getLLMTaskMappings(options?: { signal?: AbortSignal }): Promise<LLMTaskMappingsResponse> {
  return request<LLMTaskMappingsResponse>('/routing', { signal: options?.signal });
}

export function updateLLMTaskMappings(
  mappings: Array<{ action_type: string; provider_id?: string | null; model_id?: string | null }>,
): Promise<LLMTaskMappingsResponse> {
  return request<LLMTaskMappingsResponse>('/routing', {
    method: 'PUT',
    body: JSON.stringify({ mappings }),
  });
}

export function getLLMCostSummary(days = 30, options?: { signal?: AbortSignal }): Promise<LLMCostSummaryResponse> {
  return request<LLMCostSummaryResponse>(`/costs?days=${days}`, { signal: options?.signal });
}

/**
 * One provider's ceilings. Same engine session as the provider-key calls
 * (`buildEngineHeaders` on `/api/v1/settings/llm`). Null windows are unlimited.
 * `warningSupported` is false when the engine response omits `warning_percent`
 * (slice 3b not on this engine yet).
 */
export interface ProviderSpendCap {
  daily_limit_usd: string | null;
  monthly_limit_usd: string | null;
  warning_percent: number | null;
  warningSupported: boolean;
}

export interface ProviderSpendCapWrite {
  daily_limit_usd: string | null;
  monthly_limit_usd: string | null;
  /** Include the key (even when null) only if the engine advertised it. */
  warning_percent?: number | null;
}

function readUsd(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const text = value.trim();
    return text === '' ? null : text;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function readWarning(data: object): { warning_percent: number | null; warningSupported: boolean } {
  if (!Object.prototype.hasOwnProperty.call(data, 'warning_percent')) {
    return { warning_percent: null, warningSupported: false };
  }
  const raw = (data as { warning_percent?: unknown }).warning_percent;
  if (raw == null || raw === '') return { warning_percent: null, warningSupported: true };
  const value = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isInteger(value) || value < 1 || value > 99) {
    return { warning_percent: null, warningSupported: true };
  }
  return { warning_percent: value, warningSupported: true };
}

export function parseProviderSpendCap(data: unknown): ProviderSpendCap {
  const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const warning = readWarning(record);
  return {
    daily_limit_usd: readUsd(record.daily_limit_usd),
    monthly_limit_usd: readUsd(record.monthly_limit_usd),
    warning_percent: warning.warning_percent,
    warningSupported: warning.warningSupported,
  };
}

export function getProviderSpendCap(providerId: string): Promise<ProviderSpendCap> {
  return request<unknown>(`/providers/${encodeURIComponent(providerId)}/spend-cap`).then(
    parseProviderSpendCap,
  );
}

export function putProviderSpendCap(
  providerId: string,
  body: ProviderSpendCapWrite,
): Promise<ProviderSpendCap> {
  const payload: Record<string, string | null | number> = {
    daily_limit_usd: body.daily_limit_usd,
    monthly_limit_usd: body.monthly_limit_usd,
  };
  if ('warning_percent' in body) payload.warning_percent = body.warning_percent ?? null;
  return request<unknown>(`/providers/${encodeURIComponent(providerId)}/spend-cap`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }).then(parseProviderSpendCap);
}

// ── Enabled-models registry (MODEL-REGISTRY-PER-TENANT-1) ──────────────

export interface EnabledModelEntry {
  provider_id: string;
  model_id: string;
}

export interface EnabledModelsResponse {
  enabled: EnabledModelEntry[];
}

export function getEnabledModels(options?: { signal?: AbortSignal }): Promise<EnabledModelsResponse> {
  return request<EnabledModelsResponse>('/enabled-models', { signal: options?.signal });
}

export function setEnabledModels(entries: EnabledModelEntry[]): Promise<EnabledModelsResponse> {
  return request<EnabledModelsResponse>('/enabled-models', {
    method: 'PUT',
    body: JSON.stringify({ enabled: entries }),
  });
}

/**
 * Normalize a provider group to an explicit region column (Panel Details §4.4.A).
 * Desktop groups by `group.label` which may not be region-shaped; Seitiate+ surfaces
 * the region dimension explicitly. Falls back to the group label when no rule matches.
 */
export function regionForGroup(groupId: string, groupLabel: string): string {
  const id = groupId.toLowerCase();
  const label = groupLabel.toLowerCase();
  if (id.includes('americas') || /(anthropic|openai|xai|deepseek)/.test(id)) return 'Americas';
  if (id.includes('europe') || label.includes('europe') || id.includes('mistral')) return 'Europe';
  if (id.includes('asia') || label.includes('asia') || id.includes('google') || id.includes('gemini')) return 'Asia';
  if (id.includes('local') || label.includes('local') || id.includes('ollama') || id.includes('cli')) return 'Local';
  return groupLabel;
}

// ── Combined provider-settings read (seitiate#399, provider-settings.v1) ──
//
// One request that returns, for every vault-key provider, the spend cap and
// the data-choice disclosure — the read that replaces the old per-provider
// spend-limit and data-terms fan-out (the 19 Oct crash pattern). It is on
// engine main and reaches production with the engine pin bump; until then the
// live engine answers 404 and callers degrade honestly.

export interface ProviderSettingsSpendCap {
  daily_limit_usd: string | null;
  monthly_limit_usd: string | null;
  warning_percent: number | null;
}

export interface ProviderSettingsAvailableEntry {
  status: 'available';
  spend_cap: ProviderSettingsSpendCap;
  data_choice: ProviderDataChoiceState;
}

export interface ProviderSettingsUnavailableEntry {
  status: 'unavailable';
  reason: 'database' | 'invalid_data' | 'response_size';
}

export type ProviderSettingsEntry = ProviderSettingsAvailableEntry | ProviderSettingsUnavailableEntry;

export interface ProviderSettingsResponse {
  version: 'provider-settings.v1';
  providers: Record<string, ProviderSettingsEntry>;
}

export function getProviderSettings(options?: { signal?: AbortSignal }): Promise<ProviderSettingsResponse> {
  return request<ProviderSettingsResponse>('/provider-settings', { signal: options?.signal });
}
